import { Component, type ReactNode, type ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary для отлова ошибок рендеринга
 * Отображает пользовательское сообщение об ошибке вместо краша приложения
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Error info:', errorInfo);
    this.setState({ errorInfo });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      if (fallback) {
        return fallback;
      }

      return (
        <div className="flex min-h-[400px] flex-col items-center justify-center p-8 text-center">
          <div className="mb-6 rounded-full bg-red-500/10 p-4">
            <AlertTriangle className="h-12 w-12 text-red-500" />
          </div>

          <h2 className="mb-2 text-2xl font-bold text-slate-200">Что-то пошло не так</h2>

          <p className="mb-6 max-w-md text-slate-400">
            Произошла непредвиденная ошибка. Попробуйте обновить страницу или вернуться на главную.
          </p>

          {error && (
            <div className="mb-6 max-w-lg overflow-auto rounded-lg bg-slate-900 p-4 text-left">
              <code className="text-sm text-red-400">{error.message}</code>
            </div>
          )}

          <div className="flex gap-4">
            <Button variant="outline" onClick={this.handleReset} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Попробовать снова
            </Button>

            <Button onClick={this.handleGoHome} className="gap-2">
              <Home className="h-4 w-4" />
              На главную
            </Button>
          </div>
        </div>
      );
    }

    return children;
  }
}

/**
 * Специализированный Error Boundary для уроков
 */
export class LessonErrorBoundary extends ErrorBoundary {
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children } = this.props;

    if (hasError) {
      return (
        <div className="flex min-h-[300px] flex-col items-center justify-center p-6 text-center">
          <div className="mb-4 rounded-full bg-orange-500/10 p-3">
            <AlertTriangle className="h-8 w-8 text-orange-500" />
          </div>

          <h3 className="mb-2 text-lg font-semibold text-slate-200">Ошибка загрузки урока</h3>

          <p className="mb-4 text-sm text-slate-400">
            Не удалось загрузить содержимое урока. Попробуйте перезагрузить страницу.
          </p>

          {error && (
            <div className="mb-4 max-w-md overflow-auto rounded-lg bg-slate-900 p-3 text-left">
              <code className="text-xs text-red-400">{error.message}</code>
            </div>
          )}

          <div className="flex gap-3">
            <Button variant="outline" size="sm" onClick={this.handleReset} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Перезагрузить
            </Button>

            <Button size="sm" onClick={this.handleGoHome}>
              К списку уроков
            </Button>
          </div>
        </div>
      );
    }

    return children;
  }
}
